import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { usePaginatedExercises } from '../../hooks/usePaginatedExercises';
import SidebarLayout from '../layout/SidebarLayout';
import { 
  Save, 
  Tag, 
  X, 
  ArrowUp, 
  ArrowDown, 
  Plus, 
  Dumbbell, 
  Info, 
  ChevronDown, 
  ChevronUp,
  Link2, 
  AlarmClock, 
  StickyNote,
  AlertCircle,
  CheckCircle,
  Trash2,
  GripVertical
} from 'lucide-react';
import ImprovedUserExerciseForm from './ImprovedUserExerciseForm';
import NewExerciseSelector from '../common/NewExerciseSelector';
import MuscleBadges from '../common/MuscleBadges';
import { createApiClient } from '../../services/api';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Componente locale per esercizio ordinabile con drag & drop
const SortableExerciseItem = ({ exercise, previousExercise, onUpdate, onRemove, onToggleDetails, isExpanded, isFirst }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: exercise.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const noteValue = exercise.notes && typeof exercise.notes === 'object'
    ? (exercise.notes.user ?? '')
    : (exercise.note || '');

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 ${
        isDragging ? 'shadow-lg' : 'shadow-sm'
      } transition-all duration-200`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          {/* Handle per drag */}
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab hover:cursor-grabbing p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <GripVertical className="h-4 w-4" />
          </div>
          
          {/* Immagine esercizio */}
          <div className="w-12 h-12 flex-shrink-0">
            {exercise.immagine_nome ? (
              <img
                src={`/api/serve_image.php?filename=${encodeURIComponent(exercise.immagine_nome)}`}
                alt={exercise.nome}
                className="w-full h-full rounded-lg object-cover bg-gray-100 dark:bg-gray-700"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div 
              className="w-full h-full rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center hidden"
            >
              <Dumbbell className="h-6 w-6 text-gray-400" />
            </div>
          </div>
          
          {/* Info esercizio */}
          <div className="flex-1">
            <h3 className="font-medium text-gray-900 dark:text-white">{exercise.nome}</h3>
            {exercise.descrizione && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{exercise.descrizione}</p>
            )}
            
            {/* Badge muscoli */}
            <div className="mt-2">
              <MuscleBadges
                primaryMuscle={exercise.primary_muscle}
                secondaryMuscles={exercise.secondary_muscles}
                variant="compact"
                showCategory={false}
              />
            </div>
            
            {/* Badge tipo di serie */}
            <div className="mt-2 flex items-center space-x-2">
              {exercise.set_type && exercise.set_type !== 'normal' && (
                <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                  exercise.set_type === 'superset' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300' :
                  exercise.set_type === 'dropset' ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' :
                  exercise.set_type === 'circuit' ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' :
                  exercise.set_type === 'giant_set' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300' :
                  'bg-gray-100 text-gray-800 dark:bg-gray-900/40 dark:text-gray-300'
                }`}>
                  {exercise.set_type === 'superset' ? 'Superset' :
                   exercise.set_type === 'dropset' ? 'Dropset' :
                   exercise.set_type === 'circuit' ? 'Circuit' :
                   exercise.set_type === 'giant_set' ? 'Giant Set' :
                   exercise.set_type}
                </span>
              )}
              {exercise.is_cardio === 1 && (
                <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300 font-medium">
                  Cardio
                </span>
              )}
              {exercise.is_isometric_scheda === 1 && exercise.is_cardio !== 1 && (
                <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300 font-medium">
                  Isometrico
                </span>
              )}
              {exercise.linked_to_previous && (
                <span className="px-2 py-1 text-xs rounded-full bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300 font-medium">
                  🔗 Collegato
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Pulsanti azioni */}
        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={() => onToggleDetails(exercise.id)}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          
          <button
            type="button"
            onClick={() => onRemove(exercise.id)}
            className="p-2 text-red-400 hover:text-red-600 dark:hover:text-red-300 transition-colors"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Dettagli espandibili */}
      {isExpanded && (
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Serie */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Serie
              </label>
              <input
                type="number"
                min="1"
                value={exercise.serie || 3}
                onChange={(e) => onUpdate(exercise.id, 'serie', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
            
            {/* Ripetizioni o Secondi */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {(() => {
                  const isCardio = exercise.is_cardio === 1;
                  const isIsometricScheda = exercise.is_isometric_scheda === 1;
                  if (isCardio) return 'Minuti';
                  if (isIsometricScheda) return 'Secondi';
                  return 'Ripetizioni';
                })()}
              </label>
              <input
                type="number"
                min="1"
                value={exercise.ripetizioni || 12}
                onChange={(e) => onUpdate(exercise.id, 'ripetizioni', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                placeholder={(() => {
                  const isCardio = exercise.is_cardio === 1;
                  const isIsometricScheda = exercise.is_isometric_scheda === 1;
                  if (isCardio) return 'Minuti (es. 30)';
                  if (isIsometricScheda) return 'Secondi di tenuta';
                  return 'Numero ripetizioni';
                })()}
              />
            </div>
            
            {/* Peso - nascosto per esercizi cardio */}
            {exercise.is_cardio !== 1 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Peso (kg)
              </label>
              <input
                type="number"
                min="0"
                step="0.5"
                value={exercise.peso || 0}
                onChange={(e) => onUpdate(exercise.id, 'peso', parseFloat(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
            )}
            
            {/* Recupero */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Recupero (s)
              </label>
              <input
                type="number"
                min="0"
                value={exercise.tempo_recupero || 90}
                onChange={(e) => onUpdate(exercise.id, 'tempo_recupero', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>
          
          {/* Tipo di serie */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Tipo di Serie
            </label>
            <select
              value={exercise.set_type || 'normal'}
              onChange={(e) => onUpdate(exercise.id, 'set_type', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="normal">Normale</option>
              <option value="superset">Superset</option>
              <option value="dropset">Dropset</option>
              <option value="circuit">Circuit</option>
              <option value="giant_set">Giant Set</option>
            </select>
          </div>
          
          {/* Collegamento all'esercizio precedente */}
          {!isFirst && exercise.set_type && exercise.set_type !== 'normal' && previousExercise && exercise.set_type === previousExercise.set_type && (
            <div className="mt-4">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id={`linked-${exercise.id}`}
                  checked={exercise.linked_to_previous || false}
                  onChange={(e) => onUpdate(exercise.id, 'linked_to_previous', e.target.checked)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor={`linked-${exercise.id}`} className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Collegato all'esercizio precedente
                </label>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Collega questo esercizio al precedente per formare superset, dropset, ecc.
              </p>
            </div>
          )}
          
          {/* Modalità Isometrica (solo se esercizio non è cardio) */}
          {exercise.is_cardio !== 1 && (
            <div className="mt-4">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id={`is_isometric_scheda-${exercise.id}`}
                  checked={exercise.is_isometric_scheda === 1}
                  onChange={(e) => onUpdate(exercise.id, 'is_isometric_scheda', e.target.checked ? 1 : 0)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor={`is_isometric_scheda-${exercise.id}`} className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Modalità Isometrica (usa secondi invece di ripetizioni)
                </label>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-7">
                Se attivo, questo esercizio verrà eseguito a tempo (secondi) invece che a ripetizioni
              </p>
            </div>
          )}
          
          {/* Campi Cardio (solo se esercizio è cardio) */}
          {exercise.is_cardio === 1 && (
            <div className="mt-4 grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Calorie (opzionale)
                </label>
                <input
                  type="number"
                  min="0"
                  value={exercise.calorie || ''}
                  onChange={(e) => onUpdate(exercise.id, 'calorie', e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Calorie bruciate"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  BPM (opzionale)
                </label>
                <input
                  type="number"
                  min="0"
                  value={exercise.bpm || ''}
                  onChange={(e) => onUpdate(exercise.id, 'bpm', e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Battiti per minuto"
                />
              </div>
            </div>
          )}
          
          {/* Campo Note - Sistema Note Duali (Standalone: note vanno su user) */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Note
            </label>
            <textarea
              value={noteValue}
              onChange={(e) => {
                // Gestione retrocompatibilità: se esiste exercise.note (legacy), convertilo
                const existingNotes = exercise.notes || 
                  (exercise.note ? { trainer: null, user: exercise.note, system: null } : null) ||
                  { trainer: null, user: null, system: null };
                
                // 🔥 DB-001: Mantieni le note esistenti (trainer, system) quando si modifica user
                const newNotes = {
                  trainer: existingNotes.trainer || null,
                  user: e.target.value || null,
                  system: existingNotes.system || null
                };
                onUpdate(exercise.id, 'notes', newNotes);
              }}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
              placeholder="Aggiungi note per questo esercizio..."
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default function ModificaSchedaPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser, token } = useAuth();
  const { isDark } = useTheme();
  const { t } = useTranslation();
  
  const api = createApiClient(token);

  // Stati per messaggi (no NotificationContext per standalone)
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  // Stati principali
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [formData, setFormData] = useState({
    nome: '',
    descrizione: ''
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [schedaEsercizi, setSchedaEsercizi] = useState([]);
  const [muscleGroups, setMuscleGroups] = useState([]);
  const [activeTab, setActiveTab] = useState('selection');
  const [showAddExerciseForm, setShowAddExerciseForm] = useState(false);
  const [expandedExercise, setExpandedExercise] = useState(null);
  
  // WEBAPP-021: Usa hook per caricamento paginato esercizi
  const {
    exercises: esercizi,
    loading: loadingExercises,
    initialLoading: initialLoadingExercises,
    hasMore,
    loadMore,
    search: searchExercises,
    filterByGroup,
    refresh: refreshExercises
  } = usePaginatedExercises({
    initialLimit: 50,
    enableInfiniteScroll: true,
    searchDebounceMs: 500
  });
  
  // State per filtri avanzati (come in archivio esercizi)
  const [filters, setFilters] = useState({
    equipment: '',
    primaryMuscle: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  
  // Gestisce il cambio filtri da NewExerciseSelector
  const handleFiltersChange = useCallback((newFilters) => {
    const newEquipment = newFilters.equipment || '';
    const newPrimaryMuscle = newFilters.primaryMuscle || '';
    
    setFilters({
      equipment: newEquipment,
      primaryMuscle: newPrimaryMuscle
    });
    
    // Applica i filtri tramite searchExercises solo se c'è almeno un filtro attivo o ricerca
    const hasFilters = newEquipment || newPrimaryMuscle;
    const hasSearch = searchTerm && searchTerm.trim().length >= 2;
    
    if (hasFilters || hasSearch) {
      searchExercises(hasSearch ? searchTerm : '', {
        equipment: newEquipment && newEquipment.trim() !== '' ? newEquipment.trim() : undefined,
        primaryMuscle: newPrimaryMuscle && newPrimaryMuscle.trim() !== '' ? newPrimaryMuscle.trim() : undefined
      });
    } else {
      // WEBAPP-GYM-011: reset filtri senza testo ricerca → ricarica elenco completo
      searchExercises('', {});
    }
  }, [searchExercises, searchTerm]);
  
  // Gestisce la ricerca testuale
  const handleSearchChange = useCallback((term) => {
    setSearchTerm(term);
    
    // Applica ricerca con filtri attivi solo se c'è almeno ricerca (>=2 caratteri) o filtri
    const hasSearch = term && term.trim().length >= 2;
    const hasFilters = filters.equipment || filters.primaryMuscle;
    
    if (hasSearch || hasFilters) {
      searchExercises(hasSearch ? term.trim() : '', {
        equipment: filters.equipment && filters.equipment.trim() !== '' ? filters.equipment.trim() : undefined,
        primaryMuscle: filters.primaryMuscle && filters.primaryMuscle.trim() !== '' ? filters.primaryMuscle.trim() : undefined
      });
    }
  }, [searchExercises, filters]);
  
  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Caricamento dati iniziali
  useEffect(() => {
    if (id && currentUser) {
      loadSchedaData();
    }
  }, [id, currentUser]);

  const loadSchedaData = async () => {
    try {
      setLoading(true);
      
      // Carica dati scheda con esercizi
      const schedaResponse = await api.get('/schede_standalone.php', { params: { id: id } });
      if (schedaResponse.data.success) {
        const scheda = schedaResponse.data.scheda;
        const esercizi = schedaResponse.data.esercizi || [];
        
        setFormData({
          nome: scheda.nome || '',
          descrizione: scheda.descrizione || ''
        });
        
        // Mappa gli esercizi e converti linked_to_previous a boolean
          // 🔥 FASE 6: Parse note legacy in formato JSON duali (Standalone: note vanno su user)
        const mappedEsercizi = esercizi.map(ex => {
          // Parse note JSON se presenti, altrimenti gestisci legacy
          let notes = { trainer: null, user: null, system: null };
          
          if (ex.note && typeof ex.note === 'string' && ex.note.trim().startsWith('{')) {
            try {
              const parsed = JSON.parse(ex.note);
              if (parsed && typeof parsed === 'object') {
                notes = {
                  trainer: parsed.trainer || null,
                  user: parsed.user || null,
                  system: parsed.system || null
                };
              }
            } catch (e) {
              // Non è JSON valido, considera come note legacy (Standalone: note legacy vanno su user)
              notes = { trainer: null, user: ex.note, system: null };
            }
          } else if (ex.note) {
            // Note legacy (stringa semplice) → considera come note user (Standalone)
            notes = { trainer: null, user: ex.note, system: null };
          }
          
          // Se l'API restituisce già notes parsate, usale
          if (ex.notes && typeof ex.notes === 'object') {
            notes = {
              trainer: ex.notes.trainer || null,
              user: ex.notes.user || null,
              system: ex.notes.system || null
            };
          }
          
          // Per esercizi cardio: converti secondi in minuti per l'UI
          let ripetizioni = ex.ripetizioni || 12;
          if (ex.is_cardio === 1 && ripetizioni > 0) {
            // Se è cardio, ripetizioni contiene secondi nel DB, converti in minuti per l'UI
            ripetizioni = Math.round(ripetizioni / 60);
          }
          
          return {
            ...ex,
            linked_to_previous: Boolean(ex.linked_to_previous),
            notes: notes,
            // Mantieni note legacy per retrocompatibilità
            note: ex.note || (notes.user || ''),
            ripetizioni: ripetizioni, // Convertito per cardio
            is_isometric_scheda: ex.is_isometric_scheda || 0, // Da scheda_esercizi
            calorie: ex.calorie || null, // Da scheda_esercizi
            bpm: ex.bpm || null // Da scheda_esercizi
          };
        });
        
        setSchedaEsercizi(mappedEsercizi);
      }
      
      // Carica gruppi muscolari
      await loadMuscleGroups();
      
    } catch (error) {
      console.error('Errore nel caricamento dati scheda:', error);
      setMessage('Errore nel caricamento della scheda');
      setMessageType('error');
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };


  const loadMuscleGroups = async () => {
    try {
      const response = await api.get('/muscle_groups.php');
      if (response.data.success) {
        setMuscleGroups(response.data.muscle_groups || []);
      }
    } catch (error) {
      console.error('Errore nel caricamento gruppi muscolari:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Rimuovi errori di validazione quando l'utente inizia a digitare
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  const validateForm = useCallback(() => {
    const errors = {};
    
    if (!formData.nome.trim()) {
      errors.nome = 'Il nome della scheda è obbligatorio';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData.nome]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setMessage('Per favore correggi gli errori nel modulo');
      setMessageType('error');
      return;
    }

    try {
      setLoading(true);
      setMessage('');
      
      const updateData = {
        scheda_id: id,
        nome: formData.nome,
        descrizione: formData.descrizione,
        esercizi: schedaEsercizi.map((ex, index) => {
          // Assicurati che esercizio_id sia sempre definito
          const esercizio_id = ex.esercizio_id || ex.id;
          
          if (!esercizio_id) {
            console.error('Esercizio senza ID valido:', ex);
          }
          
          // 🔥 FASE 6: Converti notes in formato JSON per salvataggio
          let noteValue;
          if (ex.notes) {
            // Nuovo formato: oggetto notes
            noteValue = JSON.stringify(ex.notes);
          } else if (ex.note) {
            // Legacy: stringa semplice, converti in JSON (Standalone: note vanno su user)
            noteValue = JSON.stringify({
              trainer: null,
              user: ex.note,
              system: null
            });
          } else {
            // Nessuna nota: JSON vuoto
            noteValue = JSON.stringify({ trainer: null, user: null, system: null });
          }
          
          // Per esercizi cardio: converti minuti in secondi
          let ripetizioni = ex.ripetizioni || '';
          if (ex.is_cardio === 1 && ripetizioni) {
            // Se è cardio, ripetizioni contiene minuti nell'UI, converti in secondi per il DB
            ripetizioni = parseInt(ripetizioni) * 60;
          }
          
          return {
            esercizio_id: esercizio_id,
            serie: ex.serie || (ex.is_cardio === 1 ? 1 : ''), // Cardio: sempre 1 serie
            ripetizioni: ripetizioni,
            peso: ex.peso || '',
            tempo_recupero: ex.tempo_recupero || '',
            note: noteValue,
            set_type: ex.set_type || 'normal',
            linked_to_previous: ex.linked_to_previous || false,
            ordine: index,
            is_isometric_scheda: ex.is_isometric_scheda || 0, // 🔥 DB-001: Usa is_isometric_scheda invece di is_isometric
            calorie: ex.calorie || null,
            bpm: ex.bpm || null
          };
        })
      };

      const response = await api.put('/schede_standalone.php', updateData);
      
      if (response.data.success) {
        setMessage('Scheda aggiornata con successo!');
        setMessageType('success');
        
        // Redirect dopo un breve delay
        setTimeout(() => {
          navigate('/standalone/visualizza-schede');
        }, 1500);
      } else {
        setMessage(response.data.message || 'Errore nell\'aggiornamento della scheda');
        setMessageType('error');
      }
    } catch (error) {
      console.error('Error updating scheda:', error);
      setMessage(error.response?.data?.message || 'Errore nell\'aggiornamento della scheda');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const addEsercizioToScheda = (esercizio) => {
    // Assicurati che l'esercizio abbia un ID valido
    const esercizioId = esercizio.esercizio_id || esercizio.id;
    
    if (!esercizioId) {
      console.error('Tentativo di aggiungere esercizio senza ID valido:', esercizio);
      setMessage('Errore: esercizio non valido');
      setMessageType('error');
      return;
    }
    
    const newEsercizio = {
      ...esercizio,
      id: esercizioId, // Mantieni id per compatibilità con la visualizzazione
      esercizio_id: esercizioId, // Usa esercizio_id per l'invio al backend
      serie: esercizio.serie_default || (esercizio.is_cardio === 1 ? 1 : 3), // Cardio: sempre 1 serie
      ripetizioni: esercizio.ripetizioni_default || 12,
      peso: esercizio.peso_default || 0,
      tempo_recupero: esercizio.tempo_recupero || 90,
      set_type: 'normal',
      linked_to_previous: false,
      ordine: schedaEsercizi.length,
      // 🔥 FASE 6: Inizializza notes (Standalone: note vanno su user)
      notes: { trainer: null, user: null, system: null },
      note: '', // Mantieni per retrocompatibilità
      is_isometric_scheda: 0, // Default: non isometrico a livello scheda
      calorie: null, // Default: nessuna caloria
      bpm: null // Default: nessun BPM
    };
    
    setSchedaEsercizi(prev => [...prev, newEsercizio]);
    // WEBAPP-021: NON cambiare tab automaticamente, come nella Gym
    // setActiveTab('selected');
    
    setMessage(`Esercizio "${esercizio.nome}" aggiunto alla scheda`);
    setMessageType('success');
  };

  const removeEsercizioFromScheda = (id) => {
    setSchedaEsercizi(prev => prev.filter((ex) => ex.id !== id));
    
    // Chiudi l'esercizio se era espanso
    if (expandedExercise === id) {
      setExpandedExercise(null);
    }
    
    setMessage('Esercizio rimosso dalla scheda');
    setMessageType('success');
  };

  const updateEsercizioInScheda = (id, field, value) => {
    setSchedaEsercizi(prev => prev.map((ex) => {
      if (ex.id !== id) return ex;
      
      // Validazione: se esercizio è cardio, non può essere isometrico nella stessa scheda
      if (field === 'is_isometric_scheda' && value === 1 && ex.is_cardio === 1) {
        setMessage('Un esercizio cardio non può essere isometrico nella stessa scheda');
        setMessageType('error');
        setTimeout(() => {
          setMessage('');
          setMessageType('');
        }, 3000);
        return ex; // Non modificare
      }
      
      return { ...ex, [field]: value };
    }));
  };
  
  const handleToggleExpanded = (exerciseId) => {
    setExpandedExercise(prev => prev === exerciseId ? null : exerciseId);
  };
  
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setSchedaEsercizi(prev => {
        const oldIndex = prev.findIndex(item => item.id === active.id);
        const newIndex = prev.findIndex(item => item.id === over.id);
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  };

  const handleExerciseCreated = async (result) => {
    try {
      if (result.success && result.exercise_id) {
        // Ricarica la lista degli esercizi
        await refreshExercises();
        
        // Trova l'esercizio appena creato nella lista
        const newExercise = esercizi.find(e => e.id === result.exercise_id);
        if (newExercise) {
          // Aggiungi automaticamente alla scheda
          addEsercizioToScheda(newExercise);
        }
          
          setShowAddExerciseForm(false);
      }
    } catch (error) {
      console.error('Errore nel caricamento del nuovo esercizio:', error);
      setMessage('Errore nel caricamento del nuovo esercizio');
      setMessageType('error');
    }
  };

  if (initialLoading) {
    return (
      <SidebarLayout>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8">
          <div className="flex justify-center items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full h-full overflow-hidden flex flex-col">
        {/* Container principale */}
        <div className="flex flex-col flex-1">
          {/* Messaggio di feedback */}
          {message && (
            <div className={`mx-6 mt-4 p-4 rounded-lg ${
              messageType === 'success' 
                ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 border border-green-200 dark:border-green-800' 
                : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 border border-red-200 dark:border-red-800'
            }`}>
              <p>{message}</p>
            </div>
          )}

          {/* Header con titolo e pulsanti */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 flex-shrink-0">
            <div className="flex justify-between items-center mb-4">
              <h1 className="text-xl font-semibold text-white">
                {t('workout.editWorkoutPlan')}
              </h1>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => navigate('/standalone/visualizza-schede')}
                  className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className={`flex items-center px-6 py-2 rounded-lg font-semibold transition-colors ${
                    loading
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-white text-indigo-600 hover:bg-gray-100'
                  }`}
                >
                  {loading ? (
                    <>
                      <Save className="h-4 w-4 mr-2 inline" />
                      {t('common.saving')}
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2 inline" />
                      {t('common.save')}
                    </>
                  )}
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-white/90 mb-1">
                  {t('workout.workoutName')} *
                </label>
                <input
                  id="nome"
                  type="text"
                  name="nome"
                  placeholder={t('workout.workoutNamePlaceholder')}
                  className="w-full px-3 py-2 rounded-md focus:ring-2 focus:ring-white/50 bg-white/20 text-white placeholder-white/60 border border-white/30"
                  value={formData.nome}
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white/90 mb-1">
                  {t('workout.description')}
                </label>
                <textarea
                  id="descrizione"
                  name="descrizione"
                  placeholder={t('workout.descriptionPlaceholder')}
                  rows={2}
                  className="w-full px-3 py-2 rounded-md focus:ring-2 focus:ring-white/50 bg-white/20 text-white placeholder-white/60 border border-white/30"
                  value={formData.descrizione}
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                />
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 flex-shrink-0">
            <nav className="flex space-x-4 px-6" aria-label="Tabs">
              <button
                type="button"
                onClick={() => setActiveTab('selection')}
                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'selection'
                    ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                {t('workout.exerciseSelection')}
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('selected')}
                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'selected'
                    ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                {t('workout.selectedExercises')} ({schedaEsercizi.length})
                {schedaEsercizi.length > 0 && (
                  <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                    • {schedaEsercizi.reduce((total, ex) => total + (ex.serie || 0), 0)} serie totali
                  </span>
                )}
              </button>
            </nav>
          </div>

          {/* Contenuto delle tab */}
          <div className="flex-1 overflow-y-auto min-h-0">
            {activeTab === 'selection' && (
              <div className="h-full p-6">
                <div className="h-full max-h-[calc(95vh-200px)]">
                  <NewExerciseSelector
                    searchTerm={searchTerm}
                    exercises={esercizi}
                    onSelectExercise={addEsercizioToScheda}
                    selectedExercises={schedaEsercizi}
                    muscleGroups={muscleGroups}
                    onShowAddExerciseForm={() => setShowAddExerciseForm(true)}
                    loading={loadingExercises || initialLoadingExercises}
                    hasMore={hasMore}
                    onLoadMore={loadMore}
                    onFiltersChange={handleFiltersChange}
                    onSearchChange={handleSearchChange}
                  />
                </div>
              </div>
            )}

            {activeTab === 'selected' && (
              <div className="h-full overflow-y-auto p-6">
                {schedaEsercizi.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-gray-500 dark:text-gray-400">
                    <Dumbbell className="h-12 w-12 mb-4 opacity-50" />
                    <p className="text-lg font-medium mb-2">{t('workout.noExercisesSelected')}</p>
                    <p className="text-sm text-center max-w-md">
                      {t('workout.selectExercisesToStart')}
                    </p>
                  </div>
                ) : schedaEsercizi.length > 0 ? (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={schedaEsercizi.map(e => e.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-4">
                        {schedaEsercizi.map((exercise, index) => (
                          <SortableExerciseItem
                            key={exercise.id}
                            exercise={exercise}
                            previousExercise={index > 0 ? schedaEsercizi[index - 1] : null}
                            onUpdate={updateEsercizioInScheda}
                            onRemove={removeEsercizioFromScheda}
                            onToggleDetails={handleToggleExpanded}
                            isExpanded={expandedExercise === exercise.id}
                            isFirst={index === 0}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-gray-500 dark:text-gray-400">
                    <Dumbbell className="h-12 w-12 mb-4 opacity-50" />
                    <p className="text-lg font-medium mb-2">{t('workout.noExercisesSelected')}</p>
                    <p className="text-sm text-center max-w-md">
                      {t('workout.selectExercisesToStart')}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Modal per creazione nuovo esercizio */}
        {showAddExerciseForm && (
          <ImprovedUserExerciseForm
            onClose={() => setShowAddExerciseForm(false)}
            onSuccess={handleExerciseCreated}
          />
        )}
      </div>
    </SidebarLayout>
  );
}